import React from "react";
import { Button, Card } from "antd";
import { DownloadPage } from "./Download";
import {
  ArrowRightOutlined,
  BookOutlined,
  LineChartOutlined,
  MailOutlined,
  PictureOutlined,
  RocketOutlined,
  UserOutlined,
} from "@ant-design/icons";

type UsefulLink = {
  href: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  external?: boolean;
};

const usefulLinks: UsefulLink[] = [
  {
    href: "/account",
    title: "Account",
    description:
      "Purchase credits, update billing information, and view transactions.",
    icon: <UserOutlined />,
  },
  {
    href: "/manual",
    title: "Manual",
    description: "The complete reference for setup, gameplay, and operations.",
    icon: <BookOutlined />,
  },
  {
    href: "/",
    title: "Personal Dashboard",
    description: "Statistics and analytics for your Hero Zone location.",
    icon: <LineChartOutlined />,
  },
  {
    href: "/marketing",
    title: "Marketing Materials",
    description:
      "Posters, trailers, and other assets to promote your location.",
    icon: <PictureOutlined />,
    external: true,
  },
];

export const Welcome = () => {
  return (
    <div className="welcome-page">
      <Card className="welcome-hero" id="Download">
        <DownloadPage hideFrame />
      </Card>

      <a
        href="/quick-start"
        rel="noopener noreferrer"
        target="_blank"
        className="welcome-quickstart"
        id="Getting Started"
      >
        <div className="welcome-quickstart-icon">
          <RocketOutlined />
        </div>
        <div className="welcome-quickstart-text">
          <h3>Getting started?</h3>
          <p>
            Walk through our Quick Start Guide to set up your headsets, PC, and
            launch your first game.
          </p>
        </div>
        <Button
          type="primary"
          className="cta-button"
          size="large"
          icon={<ArrowRightOutlined />}
        >
          Open Quick Start
        </Button>
      </a>

      <section className="welcome-section" id="Useful Links">
        <header className="welcome-section-header">
          <h2>Useful Links</h2>
          <p>Jump straight to the tools and resources you use most.</p>
        </header>
        <div className="welcome-link-grid">
          {usefulLinks.map((link) => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="welcome-link-card"
            >
              <span className="welcome-link-card-icon">{link.icon}</span>
              <span className="welcome-link-card-body">
                <span className="welcome-link-card-title">
                  {link.title}
                  <ArrowRightOutlined className="welcome-link-card-arrow" />
                </span>
                <span className="welcome-link-card-desc">
                  {link.description}
                </span>
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="welcome-section" id="Support">
        <header className="welcome-section-header">
          <h2>Support</h2>
          <p>Need a hand? Reach out</p>
        </header>
        <div className="welcome-support-grid">
          <a
            className="welcome-support-card"
            href="https://wa.me/32468218710"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="welcome-support-icon welcome-support-icon--whatsapp">
              <img
                src="images/logos/Whatsapp.svg"
                alt=""
                style={{ width: 22 }}
              />
            </span>
            <span className="welcome-support-body">
              <span className="welcome-support-label">WhatsApp</span>
              <span className="welcome-support-value">+32 468 21 87 10</span>
            </span>
          </a>
          <a
            className="welcome-support-card"
            href="mailto:support@herozonevr.com"
          >
            <span className="welcome-support-icon welcome-support-icon--mail">
              <MailOutlined />
            </span>
            <span className="welcome-support-body">
              <span className="welcome-support-label">Email</span>
              <span className="welcome-support-value">
                support@herozonevr.com
              </span>
            </span>
          </a>
        </div>
      </section>

      <a
        href="https://discord.gg/hJaW2MGSma"
        rel="noopener noreferrer"
        target="_blank"
        className="welcome-discord"
        id="Discord"
      >
        <div className="welcome-discord-logo">
          <img src="images/logos/Discord.svg" alt="Discord" />
        </div>
        <div className="welcome-discord-text">
          <h3>Join our community Discord</h3>
          <p>
            Connect with other Hero Zone operators, share best practices, and
            get a direct line to the Hero Zone team. Be the first to hear about
            new releases and pitch in on what we build next.
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          className="welcome-discord-button"
          icon={<ArrowRightOutlined />}
        >
          Join the Discord
        </Button>
      </a>
    </div>
  );
};
